import { Accept } from "react-dropzone";

import bwImage from "@/assets/reference-types/bw.png";
import cannyImage from "@/assets/reference-types/canny.png";
import colorImage from "@/assets/reference-types/color.png";
import faceImage from "@/assets/reference-types/face.png";
import initImage from "@/assets/reference-types/init.png";
import lineartImage from "@/assets/reference-types/lineart.png";
import poseImage from "@/assets/reference-types/pose.png";
import segmentationImage from "@/assets/reference-types/segmentation.png";
import structureImage from "@/assets/reference-types/structure.png";
import styleImage from "@/assets/reference-types/style.png";

import { ImageReferenceTypeEnum, ReferenceMetadata } from "./types";

export const ACCEPTED_REFERENCE_IMAGE_TYPES: Accept = {
  "image/jpeg": [".jpeg", ".jpg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
};

// Total over ImageReferenceTypeEnum on purpose: a missing key is a crash at render
// (`REFERENCES_METADATA.X.label`), and a Partial map reports every key as possibly-undefined,
// so a key that is genuinely gone reads the same as one that is present. Keyed exhaustively,
// a stale key fails to compile instead. Types outside this enum (video frames, wire-only
// guidance types) go through `referenceMetadata`.
export const REFERENCES_METADATA: Record<
  ImageReferenceTypeEnum,
  ReferenceMetadata
> = {
  INIT_IMAGE: {
    label: "Reference Image",
    incompatibleReferenceTypes: ["COLOR_SKETCH"],
    referenceType: "INIT_IMAGE",
    preprocess: false,
    weight: 0.5,
    description: "Use this image as a starting point and create variations.",
    imageSrc: initImage,
    isLocalized: true,
  },
  IP_ADAPTER: {
    label: "Style Reference",
    incompatibleReferenceTypes: ["FACE"],
    referenceType: "IP_ADAPTER",
    preprocess: false,
    weight: 1,
    description: "Transfer the style of an image onto your generations.",
    imageSrc: styleImage,
    allowMultiple: true,
    isLocalized: false,
  },
  COLOR_SKETCH: {
    label: "Color Sketch",
    incompatibleReferenceTypes: [
      "IP_ADAPTER",
      "CANNY",
      "SCRIBBLE",
      "LINEART",
      "INIT_IMAGE",
    ],
    referenceType: "COLOR_SKETCH",
    preprocess: false,
    weight: 1,
    description:
      "Upload or draw a color sketch to guide your image generation.",
    imageSrc: colorImage,
    isLocalized: true,
    isDeprecated: true,
  },
  SCRIBBLE: {
    label: "B&W Sketch",
    incompatibleReferenceTypes: ["COLOR_SKETCH", "CANNY", "LINEART"],
    referenceType: "SCRIBBLE",
    preprocess: false,
    weight: 1,
    description:
      "Upload or draw a black and white sketch to guide your image generation.",
    imageSrc: bwImage,
    isLocalized: true,
    isDeprecated: true,
  },
  FACE: {
    label: "Character Face",
    incompatibleReferenceTypes: ["IP_ADAPTER"],
    referenceType: "FACE",
    preprocess: false,
    weight: 1,
    description:
      "Upload a close-up image of a character's face to generate images with this face.",
    imageSrc: faceImage,
    isLocalized: false,
    isDeprecated: true,
  },
  POSE: {
    label: "Character Pose",
    incompatibleReferenceTypes: [],
    referenceType: "POSE",
    preprocess: true,
    weight: 0.9, // recommended default conditioning scale is 0.9
    description:
      "Generate an image in a specific human pose that matches the pose of the person in your image.",
    imageSrc: poseImage,
    isLocalized: true,
  },
  DEPTH: {
    label: "Structure",
    incompatibleReferenceTypes: [],
    referenceType: "DEPTH",
    preprocess: true,
    weight: 0.8, // recommended default conditioning scale is 0.8
    description:
      "Change the appearance of your asset while maintaining its base structure.",
    imageSrc: structureImage,
    isLocalized: true,
  },
  CANNY: {
    label: "Outline",
    incompatibleReferenceTypes: ["COLOR_SKETCH", "SCRIBBLE", "LINEART"],
    referenceType: "CANNY",
    preprocess: true,
    weight: 0.7, // recommended default conditioning scale is 0.7
    description:
      "Follow the outlines of your reference image to guide your generation — great for structured concepts. This uses Canny Edge detection to extract key shapes.",
    imageSrc: cannyImage,
    isLocalized: true,
  },
  LINEART: {
    label: "Line Art",
    incompatibleReferenceTypes: ["COLOR_SKETCH", "SCRIBBLE", "CANNY"],
    referenceType: "LINEART",
    preprocess: true,
    weight: 0.7, // recommended default conditioning scale is 0.7
    description:
      "Follow your black and white sketch or line art to guide your generation. This uses your drawing directly without preprocessing.",
    imageSrc: lineartImage,
    isLocalized: true,
  },
  SEGMENTATION: {
    label: "Segmentation",
    incompatibleReferenceTypes: [],
    referenceType: "SEGMENTATION",
    preprocess: true,
    weight: 1,
    description: "Generate an image keeping the same segments of your image.",
    imageSrc: segmentationImage,
    isLocalized: true,
    isDeprecated: true,
  },
} as const;

/**
 * Metadata for a reference type that may not be an image reference — video frame types and
 * raw `GuidanceFileType` values off the wire both index this map. Returns undefined rather
 * than widening REFERENCES_METADATA itself, which would give up the exhaustiveness above.
 */
export const referenceMetadata = (
  type: string,
): ReferenceMetadata | undefined =>
  (REFERENCES_METADATA as Record<string, ReferenceMetadata | undefined>)[type];
